import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { removeDuplicatedItemsById } from '@shared/helpers/functions/remove-duplicated-items-by-id';
import { HeaderService } from '@shared/services';
import { ChartsService } from '@shared/services/charts.service';
import { QueryOptions } from 'odata-query';
import { BehaviorSubject, combineLatest, map, of, switchMap } from 'rxjs';
import {
  CountryTaxNewsWithNestedObjectsResponseODataWithMetadataResponse,
  GstarClient,
  TaxByJurisdictionResponse,
} from 'src/app/web-api-client';
import { mapTaxNewsPanelFromResponse } from '../mapper/map-tax-news-panel-from-response';
import { TaxNewsPanel } from '../models/tax-news-panel';

@Injectable({
  providedIn: 'root',
})
export class TaxNewsPanelService {
  private readonly gstarClient = inject(GstarClient);
  private readonly chartsService = inject(ChartsService);
  private readonly headerService = inject(HeaderService);

  private reloadNewsSubject: BehaviorSubject<void> = new BehaviorSubject<void>(
    undefined
  );

  taxNews$ = combineLatest([
    this.chartsService.taxJurisdictionByFilters$,
    toObservable(this.chartsService.selectedCountryCode),
    this.reloadNewsSubject,
  ]).pipe(
    switchMap(
      ([taxByJurisdictions, selectedCountryCode]: [
        TaxByJurisdictionResponse[],
        string | undefined,
        void,
      ]) => {
        if (taxByJurisdictions.length === 0) {
          return of(undefined);
        }

        const filter = this.getFilter(
          taxByJurisdictions,
          selectedCountryCode?.toLocaleUpperCase()
        );
        return this.gstarClient.byclient(
          this.headerService.selectedClient()?.id,
          undefined,
          buildQueryFilter(filter),
          undefined,
          undefined,
          'newsDate desc',
          undefined,
          undefined
        );
      }
    ),
    map(
      (
        response:
          | CountryTaxNewsWithNestedObjectsResponseODataWithMetadataResponse
          | undefined
      ): TaxNewsPanel[] | undefined => {
        return removeDuplicatedItemsById(
          response?.value ?? [],
          'countryTaxNewsId'
        ).map(value =>
          mapTaxNewsPanelFromResponse(
            value,
            this.chartsService.taxByJurisdictionData(),
            this.chartsService.selectedCountryCode()?.toLocaleUpperCase()
          )
        );
      }
    )
  );

  getFilter(
    taxByJurisdictions: TaxByJurisdictionResponse[],
    selectedCountryCode: string | undefined
  ): Partial<QueryOptions<TaxNewsPanel[]>> {
    if (selectedCountryCode) {
      return {
        filter: {
          countryTaxNewsLinks: {
            any: {
              country: {
                code: selectedCountryCode,
              },
            },
          },
        },
      };
    }
    const countryList = taxByJurisdictions.map(x => x.countryId?.toString());
    return {
      filter: {
        countryTaxNewsLinks: {
          any: {
            country: {
              countryId: { in: countryList },
            },
          },
        },
      },
    };
  }

  reloadNews(): void {
    this.reloadNewsSubject.next();
  }
}
